import { QrContentType, QrContentData, QrStyleOptions } from './types';

export const DEFAULT_QR_STYLE: QrStyleOptions = {
  foreground: '#101820',
  background: '#ffffff',
  dotStyle: 'square',
  cornerSquareStyle: 'square',
  cornerDotStyle: 'square',
  errorCorrection: 'H',
  margin: 8,
};

// A QR that is only its artwork: no background, so the product shows through it, and
// the 4-module quiet zone the QR spec asks for kept clear inside the image.
export const QR_ARTWORK_STYLE: QrStyleOptions = {
  ...DEFAULT_QR_STYLE,
  background: 'transparent',
  margin: 0,
  quietZoneModules: 4,
};

/** The style a QR element draws with: artwork only, or the original white square. */
export function getQrStyle(qrCode: { artworkOnly?: true } | undefined): QrStyleOptions {
  return qrCode?.artworkOnly ? QR_ARTWORK_STYLE : DEFAULT_QR_STYLE;
}

// Output resolution of the generated QR PNG (square). High enough for print.
export const DEFAULT_QR_SIZE = 1200;

/** A blank content record for each type, used to seed the studio forms. */
export function emptyContentFor(type: QrContentType): QrContentData {
  switch (type) {
    case 'url':
      return { type: 'url', url: '' };
    case 'text':
      return { type: 'text', text: '' };
    case 'email':
      return { type: 'email', to: '', subject: '', body: '' };
    case 'phone':
      return { type: 'phone', number: '' };
    case 'sms':
      return { type: 'sms', number: '', message: '' };
    case 'wifi':
      return { type: 'wifi', ssid: '', password: '', encryption: 'WPA', hidden: false };
    case 'vcard':
      return {
        type: 'vcard',
        firstName: '',
        lastName: '',
        organization: '',
        title: '',
        phone: '',
        email: '',
        url: '',
        address: '',
      };
    default:
      return { type: 'text', text: '' };
  }
}

/** Whether the given content has enough data to produce a meaningful QR code. */
export function isContentComplete(content: QrContentData): boolean {
  switch (content.type) {
    case 'url':
      return content.url.trim().length > 0;
    case 'text':
      return content.text.trim().length > 0;
    case 'email':
      return content.to.trim().length > 0;
    case 'phone':
      return content.number.trim().length > 0;
    case 'sms':
      return content.number.trim().length > 0;
    case 'wifi':
      return content.ssid.trim().length > 0;
    case 'vcard':
      return Boolean(content.firstName?.trim() || content.lastName?.trim() || content.email?.trim());
    default:
      return false;
  }
}
